/**
 * t555u: when the iOS app's Face ID lock asks again. The lock itself stays a per-phone choice (Me › Lock with Face ID);
 * a workspace's owners and admins choose how long the app may be away before it asks again (Settings › Security,
 * workspaces.settings.app_lock_after_min). Absent means the default, an hour.
 */

/** Minutes. 0 = every time the app opens. */
export const APP_LOCK_AFTER_OPTIONS = [0, 5, 15, 60, 240, 480] as const;
export type AppLockAfter = (typeof APP_LOCK_AFTER_OPTIONS)[number];
export const APP_LOCK_AFTER_DEFAULT: AppLockAfter = 60;

export const APP_LOCK_AFTER_LABELS: Record<AppLockAfter, string> = {
  0: 'Every time the app opens',
  5: '5 minutes',
  15: '15 minutes',
  60: '1 hour',
  240: '4 hours',
  480: '8 hours',
};

export const APP_LOCK_TEXT = {
  title: 'Face ID on the iOS app',
  label: 'Ask for Face ID again after',
  note: 'For people who turn on Lock with Face ID in the app. Time away from the app counts, even if it was closed.',
} as const;

export const isAppLockAfter = (v: unknown): v is AppLockAfter => typeof v === 'number' && (APP_LOCK_AFTER_OPTIONS as readonly number[]).includes(v);

/** A stored value as a known option (anything else, including absent, is the default). */
export const readAppLockAfter = (v: unknown): AppLockAfter => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return isAppLockAfter(n) ? n : APP_LOCK_AFTER_DEFAULT;
};

/**
 * Someone in several workspaces gets the strictest (shortest) of them: the lock is the phone's, not a workspace's.
 * No workspaces (or none sent yet): the default.
 */
export const strictestAppLockAfter = (values: readonly unknown[]): AppLockAfter =>
  values.length ? values.map(readAppLockAfter).reduce((a, b) => (b < a ? b : a)) : APP_LOCK_AFTER_DEFAULT;
